import { createAsyncThunk } from "@reduxjs/toolkit"
import { toast } from "react-toastify"
import apiCall from "../../backend/http"

export const assignDriverToSchedule = createAsyncThunk(
  "booking/assignDriverToSchedule",
  async (scheduleData, { rejectWithValue }) => {
    try {
      const response = await apiCall.post("/api/schedules/schedules/", {
        ...scheduleData,
        driver_rate: scheduleData.driver_rate || 5.0,
        status: scheduleData.status || "PENDING"
      })

      if (response.data) {
        return response.data
      }
      throw new Error("No data received from server")
    } catch (error) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data)
      }
      return rejectWithValue({ message: error.message })
    }
  }
)

export const fetchSchedules = createAsyncThunk(
  "booking/fetchSchedules",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/schedules/schedules/", {
        params: filters
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data)
    }
  }
)

export const fetchScheduleById = createAsyncThunk(
  "booking/fetchScheduleById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(`/api/schedules/schedules/${id}/`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data)
    }
  }
)

export const updateSchedule = createAsyncThunk(
  "booking/updateSchedule",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiCall.patch(
        `/api/schedules/schedules/${id}/`,
        data
      )
      toast.success("Schedule updated successfully")
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data)
    }
  }
)

export const deleteSchedule = createAsyncThunk(
  "booking/deleteSchedule",
  async (id, { rejectWithValue }) => {
    try {
      await apiCall.delete(`/api/schedules/schedules/${id}/`)
      toast.success("Schedule deleted successfully")
      return id
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete schedule")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const startSchedule = createAsyncThunk(
  "booking/startSchedule",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/schedules/schedules/${id}/start_schedule/`
      )
      toast.success("Schedule started successfully")
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to start schedule")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const endSchedule = createAsyncThunk(
  "booking/endSchedule",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/schedules/schedules/${id}/end_schedule/`
      )
      toast.success("Schedule ended successfully")
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to end schedule")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const terminateSchedule = createAsyncThunk(
  "booking/terminateSchedule",
  async ({ id, termination_reason }, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/drivers/driver-schedule/${id}/terminate/`,
        {
          termination_reason
        }
      )
      toast.success("Schedule terminated successfully")
      return response.data
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to terminate schedule"
      )
      return rejectWithValue(error.response?.data)
    }
  }
)

export const fetchInvoices = createAsyncThunk(
  "invoice/fetchInvoices",
  async (filters, { rejectWithValue, getState }) => {
    try {
      const response = await apiCall.get("/api/invoices/invoices/", {
        params: filters
      })
      return response.data
    } catch (error) {
      const errorText = error.response?.data || ""
      if (
        typeof errorText === "string" &&
        (errorText.includes("RelatedObjectDoesNotExist") ||
          errorText.includes("User has no corporate_user"))
      ) {
        return []
      }

      toast.error("Failed to load invoices. Please try again later.")
      return rejectWithValue(
        error.response?.data || { message: "Server error" }
      )
    }
  }
)

export const fetchInvoiceById = createAsyncThunk(
  "invoice/fetchInvoiceById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(`/api/invoices/invoices/${id}/`)
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch invoice")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const sendInvoiceReminder = createAsyncThunk(
  "invoice/sendReminder",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/invoices/invoices/${id}/send_email/`,
        data
      )
      toast.success("Invoice email sent successfully")
      return response.data
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to send invoice email"
      )
      return rejectWithValue(error.response?.data)
    }
  }
)

export const downloadInvoice = createAsyncThunk(
  "invoice/download",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/invoices/invoices/${id}/download/`, {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("token")
            ? `Bearer ${localStorage.getItem("token")}`
            : ""
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`)
      }

      const blob = await response.blob()

      const url = window.URL.createObjectURL(blob)

      const iframe = document.createElement("iframe")
      iframe.style.display = "none"
      document.body.appendChild(iframe)

      setTimeout(() => {
        try {
          const link = document.createElement("a")
          link.href = url
          link.download = `invoice-${id}.pdf`
          link.style.display = "none"

          iframe.contentDocument.body.appendChild(link)
          link.click()

          setTimeout(() => {
            document.body.removeChild(iframe)
            window.URL.revokeObjectURL(url)
          }, 1000)
        } catch (e) {
          document.body.removeChild(iframe)
          window.URL.revokeObjectURL(url)
        }
      }, 100)

      return id
    } catch (error) {
      toast.error("Failed to download invoice")
      return rejectWithValue(error.message || "Unknown error")
    }
  }
)

export const DriverSchedules = createAsyncThunk(
  "schedule/driverSchedules",
  async (scheduleData, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(
        "/api/drivers/driver-schedule/",
        scheduleData
      )
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to notify driver")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const getDriverSchedules = createAsyncThunk(
  "schedule/getDriverSchedules",
  async (driverId, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/schedules/", {
        params: { driver_id: driverId }
      })
      return response.data
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch driver schedules"
      )
      return rejectWithValue(error.response?.data)
    }
  }
)

export const fetchTrips = createAsyncThunk(
  "trips/fetchTrips",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/trips/")
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch trips")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const fetchTripById = createAsyncThunk(
  "trips/fetchTripById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(`/api/trips/${id}/`)
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch trip")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const startTrip = createAsyncThunk(
  "trips/startTrip",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/drivers/trips/schedule/${id}/start/`
      )
      toast.success("Trip started successfully")
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to start trip")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const completeTrip = createAsyncThunk(
  "trips/completeTrip",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/drivers/trips/schedule/${id}/complete/`
      )
      toast.success("Trip completed successfully")
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to complete trip")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const cancelTrip = createAsyncThunk(
  "trips/cancelTrip",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        `/api/drivers/trips/schedule/${id}/cancel/`
      )
      toast.success("Trip cancelled successfully")
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel trip")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const getTerminationDetails = createAsyncThunk(
  "schedules/getTerminationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(
        `/api/drivers/driver-schedule/${id}/termination_details/`
      )
      return response.data
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch termination details"
      )
      return rejectWithValue(error.response?.data)
    }
  }
)

export const updateSchedules = createAsyncThunk(
  "schedule/updateSchedules",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiCall.patch(`/api/trips/${id}/`, data)
      toast.success("Trip updated successfully")
      return response.data
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update trip")
      return rejectWithValue(error.response?.data)
    }
  }
)

export const fetchDriverDashboard = createAsyncThunk(
  "booking/fetchDriverDashboard",
  async (driver_id, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(
        `/api/drivers/drivers/${driver_id}/dashboard/`
      )

      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to load dashboard data" }
      )
    }
  }
)
